import { useState } from 'react'
import type { EventCityCreateData, EventCityRegion } from '../../services/adminApi'
import { CustomButton } from '../../components/common/CustomButton'
import { CustomTextInput } from '../../components/common/CustomTextInput'

const regions: EventCityRegion[] = ['Northeast', 'Midwest', 'South', 'West']

interface EventCityCreateModalProps {
  onClose: () => void
  onCreate: (data: EventCityCreateData) => Promise<void>
}

function CoverPreview({ url }: { url: string }) {
  const [broken, setBroken] = useState(false)

  if (!url.trim()) return null

  if (broken) {
    return (
      <div className="mt-2 flex h-16 w-24 items-center justify-center rounded-lg border border-dashed border-gray-300 dark:border-[#3d2c4f] text-xs text-gray-400">
        No preview
      </div>
    )
  }

  return (
    <img
      src={url}
      alt=""
      onError={() => setBroken(true)}
      className="mt-2 h-16 w-24 rounded-lg object-cover"
    />
  )
}

export function EventCityCreateModal({ onClose, onCreate }: EventCityCreateModalProps) {
  const [name, setName] = useState('')
  const [stateCode, setStateCode] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [region, setRegion] = useState('')
  const [coverImageUrl, setCoverImageUrl] = useState('')
  const [isActive, setIsActive] = useState(true)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async () => {
    if (!name.trim() || !stateCode.trim()) {
      setError('Name and state are required')
      return
    }
    setLoading(true)
    setError(null)
    try {
      await onCreate({
        name: name.trim(),
        state_code: stateCode.trim(),
        display_name: displayName.trim() || undefined,
        region: (region as EventCityRegion) || undefined,
        cover_image_url: coverImageUrl.trim() || undefined,
        is_active: isActive,
      })
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create city')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white dark:bg-[#1a1520] p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-heading text-xl font-bold text-gray-900 dark:text-white">Add City</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>
        )}

        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2">
              <CustomTextInput
                label="City"
                placeholder="Virginia Beach"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            <CustomTextInput
              label="State"
              placeholder="VA"
              value={stateCode}
              onChange={(e) => setStateCode(e.target.value)}
              maxLength={2}
              required
            />
          </div>
          <CustomTextInput
            label="Display Name"
            placeholder="Defaults to City, State"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Region</label>
            <select
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              className="w-full rounded-xl border border-gray-300 dark:border-[#3d2c4f] bg-white dark:bg-[#1a1520] px-4 py-2 text-sm text-gray-900 dark:text-white focus:border-[#503864] focus:outline-none focus:ring-2 focus:ring-[#503864]/20"
            >
              <option value="">No region</option>
              {regions.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>
          <div>
            <CustomTextInput
              label="Cover Image URL"
              placeholder="https://..."
              value={coverImageUrl}
              onChange={(e) => setCoverImageUrl(e.target.value)}
            />
            <CoverPreview key={coverImageUrl} url={coverImageUrl} />
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-[#503864] focus:ring-[#503864]"
            />
            Active
          </label>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <CustomButton variant="secondary" onClick={onClose}>Cancel</CustomButton>
          <CustomButton variant="primary" onClick={handleSubmit} loading={loading}>Create City</CustomButton>
        </div>
      </div>
    </div>
  )
}
